import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { PowerPurchaseState, PurchaseStep, InvoiceData, PaymentResult } from '../types/buypower.type';
import { PaymentMethod } from '../components/Utils/constants';

interface PowerPurchaseStore extends PowerPurchaseState {
  // Navigation
  setStep: (step: PurchaseStep) => void;
  nextStep: () => void;
  previousStep: () => void;
  resetFlow: () => void;
  
  // Data setters
  setAmount: (amount: number) => void;
  setPaymentMethod: (method: PaymentMethod) => void;
  setMeterNumber: (number: string) => void;
  setInvoiceData: (data: InvoiceData) => void;
  setPaymentResult: (result: PaymentResult) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  canProceed: () => boolean;
  triggerRefetch: () => void;
  triggerReset: () => void;
}

const stepOrder: PurchaseStep[] = ['amount', 'summary', 'invoice', 'payment', 'success'];

export const usePowerPurchaseStore = create<PowerPurchaseStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      step: 'amount',
      amount: 0,
      meterNumber: '',
      paymentMethod: PaymentMethod.Wallet,
      meterDetails: {
        meterNumber: '',
        meterName: '',
        address: '',
        phoneNumber: '',
        equivalentUnit: '',
        amount: 0
      },
      invoiceData: null,
      paymentResult: null,
      isLoading: false,
      error: null,
      refetchKey: 0,
      resetTrigger: 0,
      // Navigation
      setStep: (step) => set({ step }, false, 'setStep'),
      
      nextStep: () => {
        const currentIndex = stepOrder.indexOf(get().step);
        if (currentIndex < stepOrder.length - 1) {
          set({ step: stepOrder[currentIndex + 1] }, false, 'nextStep');
        }
      },
      
      previousStep: () => {
        const currentIndex = stepOrder.indexOf(get().step);
        if (currentIndex > 0) {
          set({ step: stepOrder[currentIndex - 1] }, false, 'previousStep');
        }
      },
      
      resetFlow: () => set({
        step: 'amount',
        amount: 0,
        paymentMethod: PaymentMethod.Wallet,
        meterNumber: '',
        invoiceData: null,
        paymentResult: null,
        isLoading: false,
        error: null,
        resetTrigger: get().resetTrigger + 1,
        
      }, false, 'resetFlow'),
      
      // Data setters
      setAmount: (amount) => set({ amount, error: null }, false, 'setAmount'),
      
      setMeterNumber: (meterNumber) => set({ meterNumber }, false, 'setMeterNumber'),
      
      setPaymentMethod: (method) => set({ paymentMethod: method }, false, 'setPaymentMethod'),
      
      setInvoiceData: (data) => set({ invoiceData: data }, false, 'setInvoiceData'),
      
      setPaymentResult: (result) => set({ paymentResult: result }, false, 'setPaymentResult'),
      
      setLoading: (loading) => set({ isLoading: loading }, false, 'setLoading'),
      
      setError: (error) => set({ error }, false, 'setError'),

      triggerRefetch: () => set(
        (state) => ({ refetchKey: state.refetchKey + 1 }), 
        false, 
        'triggerRefetch'
      ),
      triggerFormReset: () => set(
        (state) => ({ resetTrigger: state.resetTrigger + 1 }),
        false,
        'triggerFormReset'
      ),
      
      // Computed
      canProceed: () => {
        const state = get();
        switch (state.step) {
          case 'amount':
            return state.amount > 0 && state.paymentMethod !== null;
          case 'summary':
            return true;
          case 'invoice':
            return state.invoiceData !== null;
          case 'payment':
            return false; // External payment
          case 'success':
            return state.paymentResult !== null;
          default:
            return false;
        }
      }
    }),
    { name: 'power-purchase-store' }
  )
);